import { useCallback, useEffect, useRef } from 'react';

interface WakeLockSentinelLike extends EventTarget {
  released: boolean;
  release: () => Promise<void>;
}

interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

interface WakeLockNavigatorLike {
  wakeLock?: WakeLockLike;
}

/**
 * Keep the display awake while this page is visible.
 *
 * Browsers can drop wake locks when tabs hide, when the system sleeps or when
 * power policy changes, so the lock is reacquired whenever the page comes back.
 */
export function useScreenWakeLock() {
  const lockRef = useRef<WakeLockSentinelLike | null>(null);

  const release = useCallback(async () => {
    const lock = lockRef.current;
    if (!lock || lock.released) return;
    await lock.release();
    lockRef.current = null;
  }, []);

  const acquire = useCallback(async () => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (lockRef.current && !lockRef.current.released) return;

    const wakeLock = (navigator as WakeLockNavigatorLike).wakeLock;
    if (!wakeLock) return;

    try {
      const lock = await wakeLock.request('screen');
      lockRef.current = lock;
      lock.addEventListener('release', () => {
        if (lockRef.current === lock) lockRef.current = null;
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') return;
      console.warn('Wake lock request failed:', error);
    }
  }, []);

  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire();
      else void release();
    };

    const onInteraction = (): void => {
      void acquire();
    };

    document.addEventListener('visibilitychange', onVisibility);
    for (const eventName of ['pointerdown', 'keydown', 'touchstart'] as const) {
      window.addEventListener(eventName, onInteraction, { passive: true });
    }

    void acquire();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      for (const eventName of ['pointerdown', 'keydown', 'touchstart'] as const) {
        window.removeEventListener(eventName, onInteraction);
      }
      void release();
    };
  }, [acquire, release]);
}
